import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Minus, Star, Zap, Crown, ArrowRight } from 'lucide-react'
import { Plan } from '@/types'
import { cn } from '@/lib/utils'

export interface PlanCardProps {
  plan: Plan
  isSelected: boolean
  price: number
  discount?: number
  billingCycle: 'monthly' | 'yearly'
  onSelect: (plan: Plan) => void
  className?: string
}

interface PlanTheme {
  icon: React.ElementType
  gradient: string
  iconBg: string
  iconColor: string
  ring: string
  ctaClass: string
  badgeBg: string
}

const getPlanTheme = (planName: string): PlanTheme => {
  const normalized = planName.toLowerCase()

  switch (normalized) {
    case 'pro':
      return {
        icon: Star,
        gradient: 'from-accent/5 via-transparent to-transparent',
        iconBg: 'bg-accent/10',
        iconColor: 'text-accent',
        ring: 'ring-accent/60 shadow-accent/20',
        ctaClass: 'bg-accent text-accent-foreground hover:opacity-90',
        badgeBg: 'bg-accent text-accent-foreground',
      }
    case 'enterprise':
      return {
        icon: Crown,
        gradient: 'from-purple-500/5 via-transparent to-transparent',
        iconBg: 'bg-purple-500/10',
        iconColor: 'text-purple-600 dark:text-purple-400',
        ring: 'ring-purple-500/60 shadow-purple-500/20',
        ctaClass: 'bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600',
        badgeBg: 'bg-purple-600 text-white dark:bg-purple-500',
      }
    default:
      return {
        icon: Zap,
        gradient: 'from-muted/50 via-transparent to-transparent',
        iconBg: 'bg-muted',
        iconColor: 'text-muted-foreground',
        ring: 'ring-primary/40 shadow-primary/10',
        ctaClass: 'bg-primary text-primary-foreground hover:opacity-90',
        badgeBg: 'bg-muted text-muted-foreground',
      }
  }
}

const formatPrice = (price: number, locale: string): string => {
  if (price === 0) return 'R$ 0'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(price)
}

const formatLimit = (limit: number, locale: string, unlimitedLabel: string): string => {
  if (limit === -1) return unlimitedLabel
  return new Intl.NumberFormat(locale).format(limit)
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isSelected,
  price,
  discount,
  billingCycle,
  onSelect,
  className,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage || i18n.language || 'pt-BR'
  const isPopular = plan.name.toLowerCase() === 'pro'
  const theme = getPlanTheme(plan.name)
  const Icon = theme.icon

  const translateLevel = (prefix: string, value: string): string => {
    const key = `publicPlans.features.${prefix}.${value}`
    const translated = t(key)
    return translated === key ? value : translated
  }

  const features: Array<{ label: string; type: 'numeric' | 'boolean' | 'text'; value: string | number | boolean }> = [
    { label: t('publicPlans.features.vehicles'), type: 'numeric', value: plan.vehicleLimit },
    { label: t('publicPlans.features.users'), type: 'numeric', value: plan.userLimit },
    { label: t('publicPlans.features.leads'), type: 'numeric', value: plan.leadLimit },
    { label: t('publicPlans.features.support'), type: 'text', value: plan.supportLevel },
    { label: t('publicPlans.features.analytics'), type: 'text', value: plan.analyticsLevel },
    { label: t('publicPlans.features.customization'), type: 'boolean', value: plan.customization },
    { label: t('publicPlans.features.apiAccess'), type: 'boolean', value: plan.apiAccess },
    { label: t('publicPlans.features.multipleLocations'), type: 'boolean', value: plan.multipleLocations },
  ]

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(plan)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(plan) } }}
      className={cn(
        'group relative flex flex-col rounded-2xl border bg-card transition-all duration-300 cursor-pointer',
        'hover:-translate-y-1 hover:shadow-xl',
        isSelected
          ? `ring-2 shadow-lg ${theme.ring}`
          : 'border-border/70 shadow-sm hover:shadow-primary/10',
        isPopular && !isSelected && 'border-accent/40',
        className,
      )}
    >
      {/* Gradient overlay */}
      <div
        aria-hidden
        className={cn('absolute inset-0 rounded-2xl bg-gradient-to-b pointer-events-none', theme.gradient)}
      />

      {/* Popular badge */}
      {isPopular && (
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 z-10">
          <span className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider shadow-lg',
            theme.badgeBg,
          )}>
            <Star className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
            {t('publicPlans.badges.mostPopular')}
          </span>
        </div>
      )}

      <div className={cn('relative flex flex-col flex-1', isPopular ? 'pt-8' : 'pt-6', 'px-6 pb-6')}>
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={cn('flex h-12 w-12 items-center justify-center rounded-xl', theme.iconBg)}>
              <Icon className={cn('h-6 w-6', theme.iconColor)} strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="text-lg font-bold capitalize text-foreground">{plan.name}</h3>
              {plan.description && (
                <p className="text-xs text-muted-foreground line-clamp-1">{plan.description}</p>
              )}
            </div>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-1.5 pt-2">
            <span className="text-4xl font-bold tracking-tight text-foreground">
              {formatPrice(price, locale)}
            </span>
            <span className="text-sm text-muted-foreground">
              {billingCycle === 'yearly'
                ? t('publicPlans.summary.priceLabelYearly')
                : t('publicPlans.summary.priceLabelMonthly')}
            </span>
          </div>

          {/* Discount badge */}
          {discount != null && discount > 0 && billingCycle === 'yearly' && (
            <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              {t('publicPlans.badges.saveUpTo', { discount })}
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="my-5 h-px bg-border/70" />

        {/* Features table */}
        <div className="flex-1 overflow-hidden rounded-xl border border-border/50">
          <table className="w-full text-sm">
            <tbody>
              {features.map(({ label, type, value }, i) => (
                <tr
                  key={label}
                  className={cn(
                    'border-b border-border/30 last:border-b-0',
                    i % 2 === 0 ? 'bg-transparent' : 'bg-muted/10',
                  )}
                >
                  <td className="py-2.5 pl-3 pr-2 text-muted-foreground">{label}</td>
                  <td className="py-2.5 pr-3 pl-2 text-right font-medium text-foreground">
                    {type === 'boolean' ? (
                      value ? (
                        <Check className="ml-auto h-4 w-4 text-emerald-500" strokeWidth={2.5} />
                      ) : (
                        <Minus className="ml-auto h-4 w-4 text-muted-foreground/40" strokeWidth={1.5} />
                      )
                    ) : type === 'numeric' ? (
                      formatLimit(value as number, locale, t('publicPlans.features.unlimited'))
                    ) : (
                      translateLevel(
                        label === t('publicPlans.features.support') ? 'supportLevels' : 'analyticsLevels',
                        value as string,
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSelect(plan) }}
          className={cn(
            'mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200',
            'active:scale-[0.98]',
            isSelected
              ? `${theme.ctaClass} shadow-md`
              : 'border border-border bg-background text-foreground hover:bg-muted/50',
          )}
        >
          {isSelected ? (
            <>
              <Check className="h-4 w-4" />
              {t('publicPlans.actions.selected')}
            </>
          ) : (
            <>
              {t('publicPlans.actions.selectPlan')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}
